import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { GripVertical, Eye, EyeOff, Search, Folder } from 'lucide-react'
import { useMailboxProperties, ColumnConfig } from '@/hooks/useMailboxProperties'
import { 
  loadColumnConfigFromStorage, 
  saveColumnConfigToStorage,
  mapPropertiesToColumns 
} from '@/lib/mailbox-property-mapper'

interface MailboxColumnManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onColumnsUpdated: (columns: ColumnConfig[]) => void
}

export function MailboxColumnManagerDialog({ open, onOpenChange, onColumnsUpdated }: MailboxColumnManagerDialogProps) {
  const { properties, getPropertiesBySection, getAllSections } = useMailboxProperties()
  const [columns, setColumns] = useState<ColumnConfig[]>([])
  const [customLabels, setCustomLabels] = useState<Record<string, string>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [groupBySection, setGroupBySection] = useState(true)

  // Load configuration when dialog opens
  useEffect(() => {
    if (!open) return
    
    console.log('Loading column configuration from unified property system...')
    const { columns: loadedColumns, labels: loadedLabels } = loadColumnConfigFromStorage(properties, 'mailbox')
    
    console.log('Loaded columns:', loadedColumns)
    console.log('Loaded labels:', loadedLabels)
    
    setColumns(loadedColumns)
    setCustomLabels(loadedLabels)
  }, [open, properties])

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible } : col
    ))
  }

  const handleColumnLabelChange = (columnId: string, newLabel: string) => {
    setCustomLabels(prev => ({
      ...prev,
      [columnId]: newLabel
    }))
    
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, label: newLabel } : col
    ))
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination) return

    const items = Array.from(columns)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update order property
    const updatedColumns = items.map((col, index) => ({
      ...col,
      order: index
    }))

    setColumns(updatedColumns)
  }

  const handleSave = () => {
    console.log('Saving column configuration:', columns)
    console.log('Saving custom labels:', customLabels)
    
    // Save using unified storage system
    saveColumnConfigToStorage(columns, customLabels, 'mailbox')
    
    // Notify parent component with the updated columns
    onColumnsUpdated(columns)
    onOpenChange(false)
  }

  const handleReset = () => {
    const defaultColumns = mapPropertiesToColumns(properties)
    setColumns(defaultColumns)
    setCustomLabels({})
    
    // Clear storage
    localStorage.removeItem('mailbox-column-config')
    localStorage.removeItem('mailbox-column-labels')
  }

  const handleShowAll = () => {
    setColumns(prev => prev.map(col => ({ ...col, visible: true })))
  }

  const handleHideAll = () => {
    setColumns(prev => prev.map(col => ({ ...col, visible: false })))
  }

  // Filter columns based on search query
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columns

    return columns.filter(column => {
      const property = properties.find(p => p.id === column.id)
      const searchLower = searchQuery.toLowerCase()
      
      return (
        column.id.toLowerCase().includes(searchLower) ||
        column.label.toLowerCase().includes(searchLower) ||
        (property?.section && property.section.toLowerCase().includes(searchLower))
      )
    })
  }, [columns, searchQuery, properties])

  // Group columns by section if enabled
  const groupedColumns = useMemo(() => {
    if (!groupBySection) {
      return { 'All Columns': filteredColumns }
    }

    const grouped: Record<string, ColumnConfig[]> = {}
    
    filteredColumns.forEach(column => {
      const property = properties.find(p => p.id === column.id)
      const section = property?.section || 'Other'
      
      if (!grouped[section]) {
        grouped[section] = []
      }
      grouped[section].push(column)
    })

    return grouped
  }, [filteredColumns, groupBySection, properties])

  const visibleCount = columns.filter(col => col.visible).length
  const totalCount = columns.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">Manage Mailbox Columns</DialogTitle>
          <div className="text-sm text-muted-foreground">
            Customize which columns are visible and their order. Drag to reorder columns.
          </div>
        </DialogHeader>
        
        {/* Search and Controls */}
        <div className="space-y-4 border-b pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="px-3 py-1">
                {visibleCount} of {totalCount} visible
              </Badge>
              <div className="flex items-center gap-2">
                <Switch
                  checked={groupBySection}
                  onCheckedChange={setGroupBySection}
                />
                <Label className="text-sm">Group by section</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleShowAll}
                className="text-xs"
              >
                Show All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleHideAll}
                className="text-xs"
              >
                Hide All
              </Button>
              <Button variant="outline" size="sm" onClick={handleReset}>
                Reset to Default
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {Object.keys(groupedColumns).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No columns found matching "{searchQuery}"</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="space-y-4">
                {Object.entries(groupedColumns).map(([section, sectionColumns]) => (
                  <div key={section} className="space-y-2">
                    {groupBySection && (
                      <div className="flex items-center justify-between text-sm font-medium text-foreground border-b pb-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Folder className="h-4 w-4" />
                          {section}
                          <Badge variant="secondary" className="text-xs">
                            {sectionColumns.length}
                          </Badge>
                        </div>
                        {(() => {
                          // Get data sources for this section
                          const sources = [...new Set(sectionColumns.map(col => {
                            const property = properties.find(p => p.id === col.id)
                            return property?.dataSource
                          }).filter(Boolean))]
                          
                          if (sources.length === 0) return null
                          
                          return (
                            <div className="flex items-center gap-1">
                              {sources.map(source => {
                                const getSourceIcon = (source: string) => {
                                  switch (source) {
                                    case 'smartlead': return '🔗'
                                    case 'dashboard': return '📊'
                                    case 'calculated': return '🧮'
                                    case 'manual': return '✏️'
                                    default: return '📝'
                                  }
                                }
                                
                                const getSourceColor = (source: string) => {
                                  switch (source) {
                                    case 'smartlead': return 'bg-blue-50 text-blue-700 border-blue-200'
                                    case 'dashboard': return 'bg-green-50 text-green-700 border-green-200'
                                    case 'calculated': return 'bg-purple-50 text-purple-700 border-purple-200'
                                    case 'manual': return 'bg-gray-50 text-gray-700 border-gray-200'
                                    default: return 'bg-gray-50 text-gray-700 border-gray-200'
                                  }
                                }
                                
                                return (
                                  <div key={source} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getSourceColor(source)}`}>
                                    <span>{getSourceIcon(source)}</span>
                                    <span className="capitalize">{source}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                    
                    <Droppable droppableId={`section-${section}`}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-2"
                        >
                          {sectionColumns.map((column, index) => {
                            const globalIndex = columns.findIndex(col => col.id === column.id)
                            return (
                              <Draggable
                                key={column.id}
                                draggableId={column.id}
                                index={globalIndex}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`group flex items-center gap-3 p-3 border rounded-md bg-card transition-all ${
                                      snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : 'hover:bg-muted/50'
                                    }`}
                                  >
                                    <div
                                      {...provided.dragHandleProps}
                                      className="cursor-grab active:cursor-grabbing opacity-50 group-hover:opacity-100 transition-opacity"
                                    >
                                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                                    </div>

                                    <Switch
                                      checked={column.visible}
                                      onCheckedChange={(checked) =>
                                        handleColumnVisibilityChange(column.id, checked)
                                      }
                                    />

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-3 mb-1">
                                        <Input
                                          value={customLabels[column.id] || column.label}
                                          onChange={(e) =>
                                            handleColumnLabelChange(column.id, e.target.value)
                                          }
                                          className="h-8 text-sm font-medium"
                                          placeholder="Column name"
                                        />
                                        {(() => {
                                          const property = properties.find(p => p.id === column.id)
                                          if (!property?.dataSource) return null
                                          
                                          const getSourceIcon = (source: string) => {
                                            switch (source) {
                                              case 'smartlead': return '🔗'
                                              case 'dashboard': return '📊'
                                              case 'calculated': return '🧮'
                                              case 'manual': return '✏️'
                                              default: return '📝'
                                            }
                                          }
                                          
                                          const getSourceColor = (source: string) => {
                                            switch (source) {
                                              case 'smartlead': return 'bg-blue-50 text-blue-700 border-blue-200'
                                              case 'dashboard': return 'bg-green-50 text-green-700 border-green-200'
                                              case 'calculated': return 'bg-purple-50 text-purple-700 border-purple-200'
                                              case 'manual': return 'bg-gray-50 text-gray-700 border-gray-200'
                                              default: return 'bg-gray-50 text-gray-700 border-gray-200'
                                            }
                                          }
                                          
                                          return (
                                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getSourceColor(property.dataSource)}`}>
                                              <span>{getSourceIcon(property.dataSource)}</span>
                                              <span className="capitalize">{property.dataSource}</span>
                                            </div>
                                          )
                                        })()}
                                      </div>
                                      
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <code className="bg-muted/50 px-1.5 py-0.5 rounded text-xs">{column.id}</code>
                                        {(() => {
                                          const property = properties.find(p => p.id === column.id)
                                          if (!property?.sourceDescription) return null
                                          return <span>• {property.sourceDescription}</span>
                                        })()}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      {column.visible ? (
                                        <Eye className="h-4 w-4 text-green-600" />
                                      ) : (
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                      )}
                                      {column.pinned && (
                                        <Badge variant="secondary" className="text-xs px-2 py-0.5">
                                          Pinned
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            )
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-4 border-t bg-background">
          <div className="text-xs text-muted-foreground">
            {searchQuery && `${Object.values(groupedColumns).flat().length} of ${totalCount} columns shown`}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="min-w-[120px]">
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}